(function () {
  const MIN_SUBMIT_TOKEN_LENGTH = 4;
  const MIN_FALLBACK_TOKEN_LENGTH = 3;
  const REQUEST_TIMEOUT = 1500;
  const SEARCH_ROUTE = '/search';
  const FALLBACK_DONE_PARAM = '_partial_match';

  const normalize = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const getQueryParts = (query) => {
    const trimmed = String(query || '').trim();
    const parts = trimmed.split(/\s+/);
    const lastToken = parts[parts.length - 1] || '';

    return {
      query: trimmed,
      lastToken,
      prefix: trimmed.slice(0, trimmed.length - lastToken.length),
    };
  };

  const getRootUrl = () => {
    const root = window.FoxTheme?.routes?.root_url || window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getPredictiveSearchUrl = (query) => {
    const url = new URL(`${getRootUrl()}search/suggest.json`, window.location.origin);

    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,tag'
    );

    return url;
  };

  const fetchPredictiveResults = async (query) => {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(getPredictiveSearchUrl(query), {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });

      if (!response.ok) {
        return null;
      }

      const json = await response.json();
      return json?.resources?.results || null;
    } catch (error) {
      return null;
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const getWords = (value) =>
    String(value || '')
      .replace(/<[^>]*>/g, ' ')
      .split(/[\s/\\|,.;:()_[\]{}"'`!?+]+/)
      .map((word) => word.trim())
      .filter(Boolean);

  const getProductWords = (product) => {
    const values = [
      product?.title,
      product?.type,
      product?.vendor,
      product?.handle ? product.handle.replace(/-/g, ' ') : '',
      ...(Array.isArray(product?.tags) ? product.tags : []),
      ...(Array.isArray(product?.variants)
        ? product.variants.map((variant) => variant?.title)
        : []),
    ];

    return values.flatMap(getWords);
  };

  const findCompletionFromProducts = (query, products) => {
    const { prefix, lastToken } = getQueryParts(query);
    const normalizedToken = normalize(lastToken);

    if (normalizedToken.length < MIN_FALLBACK_TOKEN_LENGTH) {
      return null;
    }

    const matches = [];

    for (const product of products || []) {
      for (const word of getProductWords(product)) {
        const normalizedWord = normalize(word);

        if (
          normalizedWord.length > normalizedToken.length &&
          normalizedWord.startsWith(normalizedToken)
        ) {
          matches.push(word);
        }
      }
    }

    if (!matches.length) {
      return null;
    }

    matches.sort((a, b) => normalize(a).length - normalize(b).length);
    return `${prefix}${matches[0]}`;
  };

  const findCompletionFromQueries = (query, queries) => {
    const normalizedQuery = normalize(query);

    for (const suggestion of queries || []) {
      const text = suggestion?.text || '';

      if (normalize(text).startsWith(normalizedQuery) && normalize(text) !== normalizedQuery) {
        return text;
      }
    }

    return null;
  };

  const getExpandedQuery = async (query, options = {}) => {
    const results = await fetchPredictiveResults(query);

    if (!results) {
      return null;
    }

    const completion =
      findCompletionFromQueries(query, results.queries) ||
      findCompletionFromProducts(query, results.products);

    if (completion && normalize(completion) !== normalize(query)) {
      return completion;
    }

    if (options.useFirstProductTitle && results.products?.[0]?.title) {
      return results.products[0].title;
    }

    return null;
  };

  const isSearchForm = (form) => {
    if (!(form instanceof HTMLFormElement)) {
      return false;
    }

    const action = form.getAttribute('action') || '';
    return action === SEARCH_ROUTE || action.endsWith(SEARCH_ROUTE);
  };

  const shouldTrySubmitCompletion = (query) => {
    const { lastToken } = getQueryParts(query);
    return normalize(lastToken).length >= MIN_SUBMIT_TOKEN_LENGTH;
  };

  const setHiddenInput = (form, name, value) => {
    let input = form.querySelector(`input[name="${name}"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    input.value = value;
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target;

    if (!isSearchForm(form) || form.dataset.partialMatchSubmitting === 'true') {
      return;
    }

    const input = form.querySelector('input[name="q"]');
    const query = input?.value?.trim();

    if (!query || !shouldTrySubmitCompletion(query)) {
      return;
    }

    event.preventDefault();

    const expandedQuery = await getExpandedQuery(query);

    if (expandedQuery) {
      input.value = expandedQuery;
      setHiddenInput(form, 'options[prefix]', 'last');
    }

    form.dataset.partialMatchSubmitting = 'true';
    form.requestSubmit();
  });

  const runNoResultsFallback = async () => {
    const searchPage = document.querySelector('[data-search-page]');

    if (!searchPage || searchPage.dataset.searchPerformed !== 'true') {
      return;
    }

    if (Number(searchPage.dataset.searchResultsCount || 0) > 0) {
      return;
    }

    const currentUrl = new URL(window.location.href);

    if (currentUrl.searchParams.get(FALLBACK_DONE_PARAM) === '1') {
      return;
    }

    const query = searchPage.dataset.searchTerms || currentUrl.searchParams.get('q') || '';
    const { lastToken } = getQueryParts(query);

    if (normalize(lastToken).length < MIN_FALLBACK_TOKEN_LENGTH) {
      return;
    }

    const expandedQuery = await getExpandedQuery(query, { useFirstProductTitle: true });

    if (!expandedQuery || normalize(expandedQuery) === normalize(query)) {
      return;
    }

    currentUrl.searchParams.set('q', expandedQuery);
    currentUrl.searchParams.set('options[prefix]', 'last');
    currentUrl.searchParams.set(FALLBACK_DONE_PARAM, '1');
    window.location.replace(currentUrl.toString());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runNoResultsFallback);
  } else {
    runNoResultsFallback();
  }
})();
